import type {
  ToolCallContentBlock,
  ToolType,
} from "src/clients/agent-management";

/**
 * What the agent is doing while a tool call is still open, in the active
 * tense, for a live status line ("Reading the flow version").
 *
 * MCP tools are discovered at runtime, so a trace only carries the
 * model-facing tool name and its title. The verb for each tool is data here,
 * keyed by that name (workspace-services registers them in
 * `modules/mcp/infra/lambda/server/domains/*` and
 * `modules/decision_engine/lambda/mcp/*`).
 *
 * A tool this table does not name needs no entry to ship: the status heads
 * with the generic copy instead, and the agent's own narration still reads
 * under it. An entry only ever upgrades that, so the table may lag the
 * servers.
 */
export const MCP_TOOL_ACTION_VERBS: Partial<Record<string, string>> = {
  // Decision flows
  taktile_internal_flows_list_folders: "Listing the folders",
  taktile_internal_flows_list_flows: "Listing the flows",
  taktile_internal_flows_create_flow: "Creating the flow",
  taktile_internal_flows_update_flow: "Updating the flow",
  taktile_internal_flows_create_flow_version: "Creating the flow version",
  taktile_internal_flows_read_flow_version: "Reading the flow version",
  taktile_internal_flows_update_flow_version: "Updating the flow version",
  taktile_internal_flows_list_flow_version_changes:
    "Listing the flow version's changes",
  taktile_internal_flows_request_review: "Requesting a review",
  taktile_internal_flows_put_schema: "Updating the flow schema",
  taktile_internal_flows_list_parameters: "Listing the parameters",
  taktile_internal_flows_set_parameter_value: "Setting the parameter",
  taktile_internal_flows_read_node: "Reading the node",
  taktile_internal_flows_list_node_types: "Listing the node types",
  taktile_internal_flows_get_node_type_details: "Reading the node type",
  taktile_internal_flows_add_node: "Adding the node",
  taktile_internal_flows_edit_node: "Editing the node",
  taktile_internal_flows_delete_node: "Deleting the node",
  taktile_internal_flows_list_datasets: "Listing the datasets",
  taktile_internal_flows_get_dataset: "Reading the dataset",
  taktile_internal_flows_create_dataset: "Creating the dataset",
  taktile_internal_flows_edit_dataset: "Editing the dataset",
  taktile_internal_flows_delete_dataset: "Deleting the dataset",
  taktile_internal_flows_run_flow: "Running the flow",
  taktile_internal_flows_get_test_run: "Reading the test run",
  // Workspace
  taktile_internal_workspace_get_info: "Reading the workspace",
  taktile_internal_workspace_get_release_version:
    "Reading the workspace release",
  // Skills
  taktile_internal_skills_list: "Listing the skills",
  taktile_internal_skills_load: "Loading a skill",
  // Agents
  taktile_internal_agents_list_agents: "Listing the agents",
  taktile_internal_agents_list_agent_versions: "Listing the agent versions",
  taktile_internal_agents_get_agent_version: "Reading the agent version",
  taktile_internal_agents_list_models: "Listing the models",
  taktile_internal_agents_read_playground_session:
    "Reading the playground session",
  taktile_internal_agents_create_agent: "Creating the agent",
  taktile_internal_agents_update_agent: "Updating the agent",
  taktile_internal_agents_create_agent_version: "Creating the agent version",
  taktile_internal_agents_duplicate_agent_version:
    "Duplicating the agent version",
  taktile_internal_agents_update_agent_version: "Updating the agent version",
  taktile_internal_agents_invoke_agent_playground:
    "Running the agent in the playground",
  taktile_internal_ai_list_models: "Listing the AI models",
  // Case management
  taktile_internal_case_management_list_queues: "Listing the case queues",
  taktile_internal_case_management_get_queue_summary:
    "Counting the queue's cases",
  taktile_internal_case_management_list_queue_cases:
    "Listing the queue's cases",
  taktile_internal_case_management_create_queue: "Creating the case queue",
  taktile_internal_case_management_update_queue: "Updating the case queue",
  taktile_internal_case_management_list_templates: "Listing the case templates",
  taktile_internal_case_management_list_template_versions:
    "Listing the template versions",
  taktile_internal_case_management_get_template_version:
    "Reading the case template version",
  taktile_internal_case_management_create_template:
    "Creating the case template",
  taktile_internal_case_management_update_template:
    "Updating the case template",
  taktile_internal_case_management_create_template_version:
    "Adding a case template version",
  taktile_internal_case_management_update_template_version:
    "Updating the case template version",
  taktile_internal_case_management_get_flow_input_schema:
    "Building the case input schema",
  // Entities
  taktile_internal_entities_list_entity_types: "Listing the entity types",
  taktile_internal_entities_get_entity_type: "Reading the entity type",
  // Connect
  taktile_internal_connect_list_providers: "Listing the providers",
  taktile_internal_connect_get_provider_schema: "Reading the provider schema",
  taktile_internal_connect_list_connections: "Listing the connections",
  taktile_internal_connect_get_connection: "Reading the connection",
  taktile_internal_connect_create_connection: "Creating the connection",
  taktile_internal_connect_create_legacy_connection: "Creating the connection",
  taktile_internal_connect_create_custom_connection: "Creating the connection",
  taktile_internal_connect_create_webhook_connection:
    "Creating the webhook connection",
  taktile_internal_connect_create_webhook_event: "Creating the webhook event",
  taktile_internal_connect_update_webhook_event: "Updating the webhook event",
  taktile_internal_connect_create_resource_config:
    "Creating the resource config",
  taktile_internal_connect_update_resource_config:
    "Updating the resource config",
};

/** The verb for a tool the table above does not name, by its tool type. */
export const TOOL_TYPE_ACTION_VERBS: Partial<Record<ToolType, string>> = {
  flow: "Running the flow",
  read_file: "Reading the file",
  read_file_from_sandbox: "Reading the file",
  execute_code: "Running code",
  web_search: "Searching the web",
  calculator: "Calculating",
};

/**
 * The active-tense phrase for what a tool call is doing, or null when neither
 * table names it. Null rather than a generic placeholder: choosing the stand-in
 * is the caller's job, since only it knows what else the status is showing.
 */
export const toolCallActionVerb = (
  toolCall: ToolCallContentBlock,
): string | null =>
  MCP_TOOL_ACTION_VERBS[toolCall.name] ??
  TOOL_TYPE_ACTION_VERBS[toolCall.metadata.type] ??
  null;
